import { useContext, useEffect, useRef, useState } from 'react';
import axios from '@helpers/axios';
import * as Sentry from '@sentry/browser';
import { useIsFeatureEnabled } from '@helpers/flagUtils';
import { TeamContext, UserContext } from '@services/Session';
import {
  getIsFlippedFromSettings,
  getPlayerCardPosition,
} from '@helpers/utils/teamUtils';
import { Team } from '@services/Session/team';

export type ScoreboardType = 'default' | 'minimal';
export type ScoreboardPosition =
  | 'top'
  | 'bottom'
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right';

export type PlayerCardPosition =
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right';

export interface OverlaySettings {
  scoreboardType: ScoreboardType;
  scoreboardPosition: ScoreboardPosition;
  playerCardPosition: PlayerCardPosition;
}

export type StylingType = 'minimal' | 'default' | 'other';

export const useOverlaySettings = () => {
  const userStore = useContext(UserContext);
  const teamStore = useContext(TeamContext);
  const overlayEnabled = useIsFeatureEnabled('overlay_settings', userStore);

  const initialSettings = teamStore.fetchOverlaySettings;

  const [position, setPosition] = useState<ScoreboardPosition>('top');
  const [flipped, setFlipped] = useState(false);
  const [stylingType, setStylingType] = useState<StylingType>('default');
  const [isLoading, setIsLoading] = useState(true);

  const isFlipDisabled =
    stylingType === 'default' &&
    (position === 'top-right' || position === 'bottom-left');

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const didMountRef = useRef(false);

  const hasInitialized = useRef(false);

  useEffect(() => {
    if (hasInitialized.current || !teamStore.id || !initialSettings) {
      return;
    }

    const { scoreboardType, scoreboardPosition, playerCardPosition } =
      initialSettings;

    setPosition(scoreboardPosition as ScoreboardPosition);
    setFlipped(
      getIsFlippedFromSettings(
        scoreboardPosition as ScoreboardPosition,
        playerCardPosition as PlayerCardPosition,
        scoreboardType as ScoreboardType
      )
    );

    if (scoreboardType === 'default') {
      setStylingType('default');
    } else if (scoreboardType === 'minimal' && overlayEnabled) {
      setStylingType('minimal');
    } else {
      setStylingType('other');
    }

    hasInitialized.current = true;
    setIsLoading(false);
  }, [initialSettings, overlayEnabled, teamStore.id]);

  useEffect(() => {
    if (isFlipDisabled) {
      setFlipped(false);
    }
  }, [isFlipDisabled]);

  useEffect(() => {
    if (!teamStore.id || isLoading) {
      return;
    }

    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      const scoreboardType: ScoreboardType =
        stylingType === 'minimal' ? 'minimal' : 'default';

      const playerCardPosition = getPlayerCardPosition(
        position,
        flipped,
        scoreboardType
      );

      const payload: OverlaySettings = {
        scoreboardType,
        scoreboardPosition: position,
        playerCardPosition,
      };

      axios
        .post(`/v2/teams/${teamStore.id}`, {
          overlaySettings: payload,
        })
        .then(() => {
          const updatedTeam: Team = {
            ...teamStore,
            overlaySettings: payload,
          };
          teamStore.setTeamInfo(updatedTeam);
        })
        .catch((error) => {
          Sentry.captureException(error);
        });
    }, 1000);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [position, flipped, stylingType, isLoading, teamStore.id, teamStore]);

  return {
    position,
    flipped,
    stylingType,
    setPosition,
    setFlipped,
    setStylingType,
    isLoading,
    isFlipDisabled,
    overlayEnabled,
  };
};
